import LoaderInline from '@jetbrains/ring-ui/components/loader-inline/loader-inline'
import * as React from 'react'
import {useEffect} from 'react'

import {PlaceIdList} from '../../../../plugins/plugins.types'
import {getSingleBuildArg} from '../../../../rest/builds'
import {getBuildLocator} from '../../../../rest/locators'
import {restApi} from '../../../../services/rest'
import {subscribeOnOverallEvents} from '../../../../utils/subscriber'
import * as SubscriptionEvents from '../../../../utils/subscriptionEvents'
import BuildCanceled from '../../../common/BuildCanceled/BuildCanceled.container'
import BuildInfoTable from '../../../common/BuildInfoTable/BuildInfoTable'
import BuildPersonal from '../../../common/BuildPersonal/BuildPersonal.container'
import BuildPinInfo from '../../../common/BuildPinInfo/BuildPinInfo.container'
import BuildWarning from '../../../common/BuildWarning/BuildWarning.container'
import CustomizedBuildNote from '../../../common/CustomizedBuildNote/CustomizedBuildNote.lazy'
import Plugin from '../../../common/Plugin/Plugin'
import SequenceLoader, {SequenceLoaderChild} from '../../../common/SequenceLoader/SequenceLoader'
import BuildTagsList from '../../../common/TagsList/TagsList.container'
import BuildLogOverview from '../../../packages/BuildLog/BuildLogOverview/BuildLogOverview.container'
import BuildProblemsPreview from '../../../packages/BuildProblems/BuildProblemsPreview/BuildProblemsPreview'
import ChangesPreview from '../../../packages/Changes/ChangesPreview/ChangesPreview.container'
import MatrixBuild from '../../../packages/MatrixBuild/MatrixBuild'
import TestsPreviewPanel from '../../../packages/Tests/TestsPreviewPanel/TestsPreviewPanel'
import BuildComment from '../BuildComment/BuildComment.container'

import BuildOverviewProgress from './BuildOverviewProgress/BuildOverviewProgress.container'
import type {Props} from './BuildOverviewTab.types'
import BuildSnippets from './BuildSnippets/BuildSnippets.container'
import Deployments from './Deployments/Deployments.container'

import styles from './BuildOverviewTab.css'

function BuildOverviewTab({
  buildTypeId,
  buildId,
  buildLocator,
  queued,
  finished,
  composite,
  testCountsQuery,
}: Props) {
  const [fetch] = restApi.endpoints.getBuildNormalized.useLazyQuerySubscription()
  useEffect(
    () =>
      subscribeOnOverallEvents(
        [`events/${SubscriptionEvents.BUILD_QUEUE_ORDER_CHANGED};`],
        () =>
          queued &&
          buildId != null &&
          fetch(
            getSingleBuildArg(getBuildLocator(buildId), {
              withQueuedInfo: true,
            }),
          ),
      ),
    [queued, buildId, fetch],
  )

  return buildId != null && buildTypeId != null ? (
    <div className={styles.content}>
      <SequenceLoader
        loader={
          <div className={styles.loader}>
            <LoaderInline>{'Loading...'}</LoaderInline>
          </div>
        }
      >
        <SequenceLoaderChild seqKey="FirstInfoGroup" seqOff={!composite}>
          <div className={styles.firstInfoGroup}>
            <div className={styles.notes}>
              <BuildWarning className={styles.warning} buildId={buildId} inline />
              <CustomizedBuildNote withIcon />
            </div>
            <BuildInfoTable buildId={buildId} />
          </div>
        </SequenceLoaderChild>
        <SequenceLoaderChild seqKey="SecondInfoGroupWithProgress" seqOff>
          <div className={styles.secondInfoGroup}>
            <BuildPersonal className={styles.row} buildId={buildId} withIcon />
            <BuildCanceled className={styles.row} buildId={buildId} withIcon />
            <BuildPinInfo className={styles.row} buildId={buildId} withIcon />
            <BuildComment className={styles.row} buildId={buildId} withIcon />
            <BuildTagsList withAddTags className={styles.row} buildId={buildId} withIcon />
          </div>
          {!queued &&
            (composite ? (
              !finished && (
                <BuildOverviewProgress
                  className={styles.buildProgress}
                  buildId={buildId}
                  showProgressLine
                  rounded
                />
              )
            ) : (
              <BuildLogOverview className={styles.buildLog} buildId={buildId} />
            ))}
        </SequenceLoaderChild>
        <Plugin
          buildId={buildId}
          placeId={PlaceIdList.SAKURA_BUILD_OVERVIEW}
          seqOff
          seqKey="BuildPlugins"
        />
        {!queued ? <BuildSnippets buildId={buildId} seqKey="BuildSnippets" /> : null}
        <MatrixBuild buildId={buildId} seqKey="MatrixBuild" />
        <BuildProblemsPreview buildId={buildId} seqKey="BuildProblemsPreview" />
        <TestsPreviewPanel
          buildId={buildId}
          buildLocator={buildLocator}
          seqKey="BuildTestsPreview"
          countsQuery={testCountsQuery}
        />
        <ChangesPreview
          buildId={buildId}
          buildTypeId={buildTypeId}
          visibleCount={10}
          seqKey="ChangesPreview"
          withExpandableLines
          withChangesActions
          withBorder
          withPanel
        />
        <Deployments buildId={buildId} seqKey="Deployments" />
      </SequenceLoader>
    </div>
  ) : null
}

export default React.memo<Props>(BuildOverviewTab)
